import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Refresh, Category as CategoryIcon } from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { transactionsService, type TransactionWithAccount, type CreateTransactionRequest } from '../services/transactions-service';
import { accountsService } from '../services/accounts-service';
import { CategoryPicker } from '../components/CategoryPicker';
import { CategorySuggestionsPanel } from '../components/CategorySuggestionsPanel';
import { jevService } from '../services/jev-service';
import { categoryLabel, categorySourceHint, isUncategorised } from '../utils/transaction-category';
import type { Account } from '../types/api';
import { useSearch } from '@tanstack/react-router';
import type { TransactionsSearch } from '../routes/transactions';

export const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionWithAccount[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  // Filters, seeded from the URL so a dashboard link ("Groceries in October",
  // "uncategorised in the last 90 days", "needs review") lands filtered.
  const search = useSearch({ strict: false }) as TransactionsSearch;
  const [filters, setFilters] = useState({
    account_id: '',
    transaction_type: '',
    category_id: search.category_id ?? '',
    // A category NAME, for a row with no id (Uncategorised). No picker sets
    // it; it is cleared with the chip above the table.
    category: search.category ?? '',
    start_date: search.start_date ?? '',
    end_date: search.end_date ?? '',
  });

  // "Needs review": Jev answers below the household's threshold (issue #26).
  const [reviewMode, setReviewMode] = useState(search.review === '1');
  const [reviewCount, setReviewCount] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState<string | null>(null);
  const [bulkCategorizing, setBulkCategorizing] = useState(false);

  // Sorting
  type SortField = 'date' | 'account_name' | 'transaction_type' | 'amount' | 'description';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Add transaction dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState<CreateTransactionRequest>({
    account_id: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    transaction_type: 'DEPOSIT',
    category_id: '',
    description: '',
    reference_number: '',
    payee_payer: '',
  });

  const transactionTypes = [
    { value: 'DEPOSIT', label: 'Deposit' },
    { value: 'WITHDRAWAL', label: 'Withdrawal' },
    { value: 'PAYMENT', label: 'Payment' },
    { value: 'TRANSFER_IN', label: 'Transfer In' },
    { value: 'TRANSFER_OUT', label: 'Transfer Out' },
    { value: 'FEE', label: 'Fee' },
    { value: 'INTEREST', label: 'Interest' },
    { value: 'DIVIDEND', label: 'Dividend' },
    { value: 'REFUND', label: 'Refund' },
  ];

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await accountsService.getAccounts();
      const accountsData = response?.data?.data || [];
      const assetDebtAccounts = accountsData.filter(
        (acc: Account) => acc.account_type === 'Asset' || acc.account_type === 'Debt'
      );
      setAccounts(assetDebtAccounts);
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await transactionsService.getTransactions({
        account_id: filters.account_id || undefined,
        transaction_type: filters.transaction_type || undefined,
        category_id: filters.category_id || undefined,
        category: filters.category || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page: page + 1,
        per_page: rowsPerPage,
      });
      
      let transactionsData = response?.data?.data || [];
      const totalData = response?.data?.total || 0;
      
      // Apply sorting
      transactionsData = [...transactionsData].sort((a: TransactionWithAccount, b: TransactionWithAccount) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortField) {
          case 'date':
            aValue = new Date(a.date).getTime();
            bValue = new Date(b.date).getTime();
            break;
          case 'account_name':
            aValue = (a.account_name || '').toLowerCase();
            bValue = (b.account_name || '').toLowerCase();
            break;
          case 'transaction_type':
            aValue = (a.transaction_type || '').toLowerCase();
            bValue = (b.transaction_type || '').toLowerCase();
            break;
          case 'amount':
            aValue = parseFloat(a.amount || '0');
            bValue = parseFloat(b.amount || '0');
            break;
          case 'description':
            aValue = (a.description || '').toLowerCase();
            bValue = (b.description || '').toLowerCase();
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      setTransactions(transactionsData);
      setTotalRows(totalData);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
      // Extract error message from ApiError or generic Error
      let errorMessage = 'Failed to fetch transactions';
      if (err && typeof err === 'object') {
        if (err.message) {
          errorMessage = err.message;
        } else if (err.error) {
          errorMessage = err.error;
        } else if (err.details?.message) {
          errorMessage = err.details.message;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setTransactions([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    // Best effort: a household without Jev simply has an empty queue.
    jevService.listSuggestions()
      .then((r) => setReviewCount(r?.total ?? 0))
      .catch(() => setReviewCount(0));
  }, []);

  const leaveReview = () => {
    setReviewMode(false);
    fetchTransactions();
  };

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleAddTransaction = async () => {
    if (!addForm.account_id || !addForm.date || !addForm.amount || !addForm.transaction_type) {
      setError('Please fill in all required fields');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      const payload: CreateTransactionRequest = {
        account_id: addForm.account_id,
        date: addForm.date,
        amount: addForm.amount,
        transaction_type: addForm.transaction_type,
      };

      if (addForm.category_id) payload.category_id = addForm.category_id;
      if (addForm.description) payload.description = addForm.description;
      if (addForm.reference_number) payload.reference_number = addForm.reference_number;
      if (addForm.payee_payer) payload.payee_payer = addForm.payee_payer;

      await transactionsService.createTransaction(payload);
      
      setAddDialogOpen(false);
      setSuccessMessage('Transaction added successfully');
      setAddForm({
        account_id: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        transaction_type: 'DEPOSIT',
        category_id: '',
        description: '',
        reference_number: '',
        payee_payer: '',
      });
      fetchTransactions();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Failed to add transaction';
      setError(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: string, currency: string = 'CAD') => {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(numAmount);
  };

  const getAmountColor = (amount: string) => {
    const numAmount = parseFloat(amount);
    if (numAmount > 0) return 'success.main';
    if (numAmount < 0) return 'error.main';
    return 'text.primary';
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(transactions.map((t) => t.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleBulkCategorize = async () => {
    if (selected.length === 0) return;
    setBulkCategorizing(true);
    try {
      const result = await transactionsService.bulkCategorize(selected, bulkCategoryId);
      const updated = (result as any)?.data?.updated ?? (result as any)?.updated ?? selected.length;
      setSuccessMessage(`Categorized ${updated} transactions`);
      setBulkCategoryDialogOpen(false);
      setSelected([]);
      setBulkCategoryId(null);
      fetchTransactions();
    } catch (err: any) {
      setError(err?.message || 'Failed to bulk categorize');
    } finally {
      setBulkCategorizing(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage transactions for Asset and Debt accounts
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {(reviewCount > 0 || reviewMode) && (
              <Chip
                clickable
                color={reviewMode ? 'primary' : 'default'}
                variant={reviewMode ? 'filled' : 'outlined'}
                label={`Needs review (${reviewCount})`}
                onClick={() => (reviewMode ? leaveReview() : setReviewMode(true))}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTransactions}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              disabled={accounts.length === 0}
            >
              Add Transaction
            </Button>
          </Box>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {reviewMode && <CategorySuggestionsPanel onCountChange={setReviewCount} />}

        {/* Filters */}
        <Card sx={{ mb: 3, display: reviewMode ? 'none' : undefined }}>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Account</InputLabel>
                <Select
                  value={filters.account_id}
                  label="Account"
                  onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  value={filters.transaction_type}
                  label="Transaction Type"
                  onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <CategoryPicker
                value={filters.category_id || null}
                onChange={(categoryId) => setFilters({ ...filters, category_id: categoryId || '' })}
                label="Category"
                size="small"
              />

              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                label="End Date"
                type="date"
                size="small"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            {filters.category && (
              <Chip
                sx={{ mt: 2 }}
                size="small"
                label={`Category: ${filters.category}`}
                onDelete={() => setFilters({ ...filters, category: '' })}
              />
            )}
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden', display: reviewMode ? 'none' : undefined }}>
          {selected.length > 0 && (
            <Toolbar sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', minHeight: '48px !important' }}>
              <Typography sx={{ flex: 1 }} variant="subtitle1">
                {selected.length} selected
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<CategoryIcon />}
                onClick={() => setBulkCategoryDialogOpen(true)}
              >
                Categorize
              </Button>
            </Toolbar>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No transactions found. Add a transaction to get started.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selected.length > 0 && selected.length < transactions.length}
                          checked={transactions.length > 0 && selected.length === transactions.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'date'}
                          direction={sortField === 'date' ? sortDirection : 'asc'}
                          onClick={() => handleSort('date')}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'account_name'}
                          direction={sortField === 'account_name' ? sortDirection : 'asc'}
                          onClick={() => handleSort('account_name')}
                        >
                          Account
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'transaction_type'}
                          direction={sortField === 'transaction_type' ? sortDirection : 'asc'}
                          onClick={() => handleSort('transaction_type')}
                        >
                          Type
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={sortField === 'amount'}
                          direction={sortField === 'amount' ? sortDirection : 'asc'}
                          onClick={() => handleSort('amount')}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={sortField === 'description'}
                          direction={sortField === 'description' ? sortDirection : 'asc'}
                          onClick={() => handleSort('description')}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Payee/Payer</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => {
                      const isSelected = selected.includes(transaction.id);
                      return (
                        <TableRow key={transaction.id} hover selected={isSelected}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleSelectOne(transaction.id)}
                            />
                          </TableCell>
                          <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {transaction.account_name || 'Unknown Account'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transactionTypes.find(t => t.value === transaction.transaction_type)?.label || transaction.transaction_type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {/* category_name is resolved by the API (assigned
                                category, else mapped bank label); the raw bank
                                label only appears in the tooltip. */}
                            <Tooltip title={categorySourceHint(transaction) ?? ''}>
                              {isUncategorised(transaction.category_name) ? (
                                <Typography variant="body2" color="text.disabled">
                                  {categoryLabel(transaction)}
                                </Typography>
                              ) : (
                                <Chip
                                  label={categoryLabel(transaction)}
                                  size="small"
                                  variant={transaction.category_id ? 'filled' : 'outlined'}
                                  sx={{
                                    bgcolor: transaction.category_color && transaction.category_id ? `${transaction.category_color}20` : undefined,
                                    color: transaction.category_color || 'text.primary',
                                    border: transaction.category_color ? `1px solid ${transaction.category_color}40` : undefined,
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              color={getAmountColor(transaction.amount)}
                            >
                              {formatCurrency(transaction.amount, transaction.account_currency)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {transaction.description || '-'}
                          </TableCell>
                          <TableCell>
                            {transaction.payee_payer || '-'}
                          </TableCell>
                          <TableCell>
                            {transaction.reference_number || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      </Box>

      {/* Add Transaction Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Account</InputLabel>
              <Select
                value={addForm.account_id}
                label="Account"
                onChange={(e) => setAddForm({ ...addForm, account_id: e.target.value })}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name} ({account.account_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Date"
              type="date"
              value={addForm.date}
              onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth required>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={addForm.transaction_type}
                label="Transaction Type"
                onChange={(e) => setAddForm({ ...addForm, transaction_type: e.target.value })}
              >
                {transactionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Amount"
              type="number"
              value={addForm.amount}
              onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.01' }}
              helperText="Positive for credits (deposits), negative for debits (withdrawals)"
            />

            <CategoryPicker
              value={addForm.category_id || null}
              onChange={(categoryId) => setAddForm({ ...addForm, category_id: categoryId || '' })}
              label="Category (Optional)"
              size="medium"
            />

            <TextField
              label="Description (Optional)"
              value={addForm.description || ''}
              onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Payee/Payer (Optional)"
              value={addForm.payee_payer || ''}
              onChange={(e) => setAddForm({ ...addForm, payee_payer: e.target.value })}
              fullWidth
            />

            <TextField
              label="Reference Number (Optional)"
              value={addForm.reference_number || ''}
              onChange={(e) => setAddForm({ ...addForm, reference_number: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddTransaction}
            variant="contained"
            disabled={adding || !addForm.account_id || !addForm.date || !addForm.amount || !addForm.transaction_type}
          >
            {adding ? <CircularProgress size={20} /> : 'Add Transaction'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Categorize Dialog */}
      <Dialog open={bulkCategoryDialogOpen} onClose={() => setBulkCategoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Categorize {selected.length} Transactions</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <CategoryPicker
              value={bulkCategoryId}
              onChange={(categoryId) => setBulkCategoryId(categoryId)}
              label="Category"
              size="medium"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCategoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBulkCategorize}
            variant="contained"
            disabled={bulkCategorizing}
          >
            {bulkCategorizing ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

